import React, { useState, useEffect } from 'react';
import { Modal, Typography, Tooltip, Space } from 'antd';
import { FullscreenOutlined, ExpandAltOutlined } from '@ant-design/icons';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.js';

const PdfPreview = ({ bucketUrl, width = 200, height = 260, title,
                    scale=0.3 }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Custom plugin to remove toolbar and other controls for preview
  const previewPluginInstance = {
    renderToolbar: () => null,
    renderError: () => null,
    renderLoader: () => null
  };

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        const response = await fetch(bucketUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
      } catch (error) {
        console.error("Error fetching the PDF:", error);
      }
    };

    if (bucketUrl) {
      fetchPdf();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [bucketUrl]);

  return (
    <>
      <div className="overflow-hidden"
        style={{
  position: 'relative',
  textAlign: 'left',
  padding: '10px',
  width: width,
  minWidth: width,
  maxWidth: width
}}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {pdfUrl ? (
          <Space direction="vertical">
            <div onClick={() => setIsModalVisible(true)} style={{ cursor: 'pointer' }}>
              <div className={'sunken-shadow'}
                style={{
                  height,
                  width,
                  overflow: 'hidden',
                  position: 'relative',
                  borderRadius: '4px',
                }}
              >
                <Worker workerUrl={workerSrc}>
                  <Viewer
                    fileUrl={pdfUrl}
                    plugins={[previewPluginInstance]}
                    defaultScale={scale}
                    renderToolbar={() => null}
                    renderError={() => null}
                    renderLoader={() => null}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </Worker>

                {isHovered && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      display: 'flex',
                      gap: '8px',
                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      padding: '4px',
                      borderRadius: '4px'
                    }}
                  >
                    <Tooltip title="Expand in Current Tab">
                      <FullscreenOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsModalVisible(true);
                        }}
                        style={{ fontSize: '16px', cursor: 'pointer' }}
                      />
                    </Tooltip>
                    <Tooltip title="Open in New Tab">
                      <ExpandAltOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(pdfUrl, "_blank", "noopener,noreferrer");
                        }}
                        style={{ fontSize: '16px', cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>

            <div style={{ height: 30, width }}>
              <Tooltip title={title}>
                <Typography.Text ellipsis={{ rows: 2, expandable: false }}>
                  {title || "Untitled.."}
                </Typography.Text>
              </Tooltip>
            </div>
          </Space>
        ) : (
          <div>Loading PDF...</div>
        )}
      </div>

      <Modal
        title="PDF Fullscreen View"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width="80%"
        style={{
          top: 20,
          maxWidth: '1200px'
        }}
        bodyStyle={{
          padding: 0,
          height: 'calc(90vh - 110px)',
          overflow: 'auto'
        }}
      >
        <Worker workerUrl={workerSrc}>
          <Viewer
            fileUrl={pdfUrl}
            plugins={[defaultLayoutPluginInstance]}
          />
        </Worker>
      </Modal>
    </>
  );
};

export default PdfPreview;